/**
 * InlineGameHeader - compact turn/score info for the unified header bar
 * (also hosts the single in-game pause button)
 */

import { useMemo } from 'react';
import { motion } from 'framer-motion';
import { useShallow } from 'zustand/react/shallow';
import { useGameStore, selectCurrentTurnPlayerId } from '../../stores/gameStore';
import { socketActions } from '../../lib/socketActions';
import { BotIcon } from '../ui/BotIcon';
import { TurnCountdown } from './TurnCountdown';
import clsx from 'clsx';

const togglePause = () => socketActions.togglePause();
const endGameEarly = () => socketActions.endGameEarly();

export function InlineGameHeader() {
  const playerId = useGameStore((s) => s.playerId);
  const status = useGameStore((s) => s.gameState?.status);
  const currentTurnPlayerId = useGameStore(selectCurrentTurnPlayerId);
  const players = useGameStore((s) => s.gameState?.players);
  const tilesLeft = useGameStore((s) => (s.gameState ? s.gameState.vowelsRemaining + s.gameState.consonantsRemaining : 0));
  const isPaused = useGameStore((s) => s.gameState?.pausedPlayerIds?.includes(s.playerId || '') ?? false);
  // [id, score, wordCount] tuples flattened for cheap shallow comparison
  const scoreData = useGameStore(
    useShallow((s) => {
      const gs = s.gameState;
      if (!gs) return [] as (string | number)[];
      return gs.players.flatMap((p) => [p.id, gs.scores[p.id] || 0, gs.playerWords[p.id]?.length || 0]);
    })
  );

  const sortedPlayers = useMemo(() => {
    if (!players) return [];
    const byId = new Map(players.map((p) => [p.id, p]));
    const rows: { id: string; name: string; isAI: boolean; score: number; wordCount: number }[] = [];
    for (let i = 0; i < scoreData.length; i += 3) {
      const p = byId.get(scoreData[i] as string);
      if (!p) continue;
      rows.push({ id: p.id, name: p.name, isAI: p.isAI, score: scoreData[i + 1] as number, wordCount: scoreData[i + 2] as number });
    }
    return rows.sort((a, b) => b.score - a.score);
  }, [players, scoreData]);

  if (status !== 'playing' || !players || !currentTurnPlayerId) return null;

  const currentPlayer = players.find((p) => p.id === currentTurnPlayerId);
  const isMyTurn = currentTurnPlayerId === playerId;

  // Check if all other players are AI (enables "End Game" button)
  const otherPlayers = players.filter((p) => p.id !== playerId);
  const isVsAIOnly = otherPlayers.length > 0 && otherPlayers.every((p) => p.isAI);

  return (
    <div className="flex-1 min-w-0 flex items-center justify-start sm:justify-center gap-1.5 sm:gap-3 mx-1 sm:mx-2 overflow-hidden">
      {/* Turn indicator - compact */}
      <motion.div
        className={clsx(
          'px-2 py-0.5 rounded text-xs font-medium border shrink-0 min-w-0',
          isMyTurn
            ? 'bg-green-100 text-green-800 border-green-400 dark:bg-green-900/30 dark:text-green-400 dark:border-green-600'
            : 'bg-amber-100 text-amber-800 border-amber-400 dark:bg-amber-900/30 dark:text-amber-400 dark:border-amber-600'
        )}
        animate={isMyTurn ? { scale: [1, 1.02, 1] } : undefined}
        transition={isMyTurn ? { duration: 0.5, repeat: Infinity } : undefined}
      >
        <span className="font-bold">Turn:</span>{' '}
        <span className="max-w-[60px] sm:max-w-[100px] truncate inline-block align-bottom">
          {isMyTurn ? 'You!' : currentPlayer?.name || '...'}
        </span>
      </motion.div>

      {/* Turn countdown */}
      <TurnCountdown />

      {/* Pause button (the only in-game pause control) */}
      <button
        onClick={togglePause}
        className={clsx(
          'px-2 py-1 rounded text-xs font-medium border transition-colors shrink-0',
          isPaused
            ? 'bg-amber-100 text-amber-800 border-amber-400 dark:bg-amber-900/30 dark:text-amber-400 dark:border-amber-600 hover:bg-amber-200 dark:hover:bg-amber-900/50'
            : 'bg-slate-100 text-slate-600 border-slate-300 dark:bg-slate-800 dark:text-slate-400 dark:border-slate-600 hover:bg-slate-200 dark:hover:bg-slate-700'
        )}
        title={isPaused ? 'Resume (Esc)' : 'Pause (Esc)'}
        aria-label={isPaused ? 'Resume game' : 'Pause game'}
      >
        <span aria-hidden="true">{isPaused ? '▶' : '⏸'}</span>
        <span className="hidden sm:inline ml-1">{isPaused ? 'Resume' : 'Pause'}</span>
        <span className="hidden lg:inline text-[10px] opacity-60 ml-0.5">(Esc)</span>
      </button>

      {/* End Game button - only when playing vs AI only */}
      {isVsAIOnly && (
        <button
          onClick={endGameEarly}
          className="px-2 py-1 rounded text-xs font-medium border transition-colors shrink-0 bg-red-100 text-red-700 border-red-300 dark:bg-red-900/30 dark:text-red-400 dark:border-red-600 hover:bg-red-200 dark:hover:bg-red-900/50"
          title="End game immediately"
        >
          <span className="hidden xs:inline">End Game</span>
          <span className="xs:hidden">End</span>
        </button>
      )}

      {/* Tiles remaining - hidden on very small screens */}
      <div className="hidden xs:flex text-xs text-slate-500 dark:text-slate-400 shrink-0">
        {tilesLeft > 0 ? (
          <span>{tilesLeft} left</span>
        ) : (
          <span className="text-amber-600 dark:text-amber-400 font-medium">Ending!</span>
        )}
      </div>

      {/* Inline scores - only where there is room */}
      <div className="hidden lg:flex items-center gap-1 text-xs flex-nowrap justify-end min-w-0 overflow-hidden">
        <span className="text-slate-400 dark:text-slate-500 shrink-0">Scores:</span>
        {sortedPlayers.map((player, index) => (
          <div
            key={player.id}
            className={clsx(
              'flex items-center gap-0.5 px-1.5 py-0.5 rounded-full shrink-0',
              player.id === playerId
                ? 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-400'
                : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400'
            )}
          >
            <span className="text-slate-400 dark:text-slate-500">#{index + 1}</span>
            {player.isAI && <BotIcon size="xs" className="text-slate-400" />}
            <span className="max-w-[60px] truncate" title={player.name}>{player.name}</span>
            <span className="font-bold text-blue-600 dark:text-blue-400">{player.score}</span>
            <span className="text-slate-400 dark:text-slate-500">({player.wordCount})</span>
          </div>
        ))}
      </div>
    </div>
  );
}
